import { useMemo } from 'react'
import {
  useGetEachRepositoryScorecards,
  useGetEachRepositoryThreatModelContent,
  useGetRepositoryScorecards,
  useGetRepositoryThreatModelContent,
} from '../utils/api-client/api-hooks.ts'
import type {
  Repository,
  ScorecardsPhase,
  ScorecardsResponse,
  ScorecardsStatus,
  ThreatModelContent,
} from '../utils/api-client/api-schemas.ts'
import { riskFromScorecard, weakestDimension } from './risk-from-scorecard.ts'

/**
 * A risk score per repository, derived from each one's threat-model scorecard.
 *
 * **Two requests per repository, deliberately.** There is no org-level endpoint for either half: the
 * scorecard carries the dimension scores, and `exposure.internet_facing` lives on the threat model's
 * `/content`. That cost is bounded by the page rather than the estate — the repositories list is
 * already paginated, and only the rows on screen are asked about.
 *
 * The alternative — score from posture immediately and fold exposure in once content arrives — is
 * rejected: a score that visibly changes under the reader is worse than one that takes a moment. That
 * is not just a preference. `createRiskComparator` sorts on these numbers, so a score that firms up
 * after render would reorder the ranked view row by row as each exposure landed.
 */

/**
 * Two states the backend does not report, because they are about this page rather than the run:
 * `loading` is the request still in flight, and `absent` is a repository with no threat model and so no
 * run to report. They are kept apart because a pending row that read `absent` would flash "no threat
 * model" on every page load before flipping to the truth.
 */
export type RepositoryScorecardState = ScorecardsStatus | 'absent' | 'loading'

export type RepositoryRiskScore = {
  /** 0–100, higher is riskier. Absent unless the run completed with a usable score. */
  riskScore: number | undefined
  state: RepositoryScorecardState
  /** Which stage a `processing` run has reached, so the row says more than "generating". */
  phase: ScorecardsPhase | undefined
  /** The dimension holding the score back, and when the run produced it — the row's own detail. */
  weakest: { displayName: string; score: number } | undefined
  generatedAt: string | undefined
  /** Why a run failed. Short in practice — every reported one is a single sentence. */
  failureReason: string | undefined
}

/**
 * One repository's score, for its own row.
 *
 * Separate from the fan-out below rather than plumbed down from it: the cell mounting its own query is
 * what keeps the column definitions free of page state, and react-query serves both from one cache, so
 * the row costs no request the page has not already made.
 */
export const useRepositoryRiskScore = (repositoryId: string): RepositoryRiskScore => {
  const { data: response, isPending } = useGetRepositoryScorecards({ repositoryId, retry: false })
  // `isLoading` rather than `isPending`: a query held back by `enabled` stays pending forever, which would
  // pin the row at `loading` for every unmodelled repository. `isLoading` is pending *and* fetching, so it
  // is true only while a request is genuinely outstanding — and false again if that request fails, which
  // lets the score settle on the `unknown` weight rather than hanging.
  const { data: content, isLoading: isExposureLoading } = useGetRepositoryThreatModelContent({
    repositoryId,
    enabled: !!response?.scorecard,
    retry: false,
  })
  const internetFacing = content?.exposure.internet_facing

  return useMemo(
    () => toRiskScore({ response, isPending, internetFacing, isExposureLoading }),
    [response, isPending, internetFacing, isExposureLoading]
  )
}

/** The one place a scorecard response becomes a row's score, so the two call paths cannot disagree. */
const toRiskScore = ({
  response,
  isPending,
  internetFacing,
  isExposureLoading,
}: {
  response: ScorecardsResponse | null | undefined
  isPending: boolean
  internetFacing: string | undefined
  isExposureLoading: boolean
}): RepositoryRiskScore => {
  const scorecard = response?.scorecard
  const weakest = scorecard ? weakestDimension(scorecard.dimensions) : undefined
  /**
   * Exposure is the second half of the score, and it cannot even be requested until the scorecard names
   * a completed run. Publishing a number before it arrives would weight by `unknown` (0.8) and then jump
   * to the real weight — visible as a changing number, and as rows reordering in the ranked view.
   */
  const isAwaitingExposure = !!scorecard && isExposureLoading

  return {
    weakest: weakest ? { displayName: weakest.displayName, score: weakest.score } : undefined,
    generatedAt: scorecard?.generatedAt,
    failureReason: response?.failureReason,
    // Pending first, then `null` — the 404 the deserializer maps, meaning asked and there is no threat
    // model to score. Only after both is a reported status the answer.
    state: isPending || isAwaitingExposure ? 'loading' : response ? response.status : 'absent',
    phase: response?.status === 'processing' ? response.phase : undefined,
    // `scorecard` is absent until the run completes, so `processing` and `failed` yield no score rather
    // than yielding a zero — zero is the safest value on this scale.
    riskScore:
      scorecard && !isAwaitingExposure
        ? riskFromScorecard({ dimensions: scorecard.dimensions, internetFacing })
        : undefined,
  }
}

/**
 * Declared at module scope, not inline.
 *
 * `useQueries` memoises `combine` against the function's identity. An inline arrow is a new function
 * every render, so the library cannot memoise and returns a fresh object each time — which changed the
 * `useMemo` dependencies below, produced a new `Map`, re-rendered, and ran `combine` again. The second
 * fan-out's input reads the first's output, so the two drove each other in an endless loop.
 */
const combineScorecards = (results: { data?: ScorecardsResponse | null; isPending: boolean }[]) => ({
  scorecards: results.map(result => ({ response: result.data, isPending: result.isPending })),
})

const combineExposures = (results: { data?: ThreatModelContent; isLoading: boolean }[]) => ({
  exposures: results.map(result => ({
    internetFacing: result.data?.exposure.internet_facing,
    isLoading: result.isLoading,
  })),
})

export const useRepositoryRiskScores = (repositories: Repository[]): Map<string, RepositoryRiskScore> => {
  const { scorecards } = useGetEachRepositoryScorecards({
    repositoryIds: repositories.map(repository => repository.id),
    // `refetchInterval` is deliberately *not* overridden. The options' own `getScorecardsRefetchInterval`
    // polls only while a run is `processing`, so the cost is the number of repositories actually
    // generating on this page — normally none — rather than the page size. That polling is what makes
    // `phase` truthful; frozen at page load it would claim progress that is not happening.
    //
    // A repository with no threat model answers 404, which is expected rather than a fault, so retrying
    // spends requests on a known answer.
    retry: false,
    combine: combineScorecards,
  })

  /**
   * Only the repositories whose scorecard actually completed. Asking for the subset rather than fanning
   * out over everything and disabling most of it keeps the second fan-out narrower than the first, so an
   * unmodelled repository costs one request rather than two — and it is per repository rather than behind
   * a barrier, so each request goes out as soon as its own scorecard resolves.
   */
  const scoredRepositoryIds = useMemo(
    () => repositories.filter((_, index) => !!scorecards[index]?.response?.scorecard).map(repository => repository.id),
    [repositories, scorecards]
  )

  const { exposures } = useGetEachRepositoryThreatModelContent({
    repositoryIds: scoredRepositoryIds,
    retry: false,
    combine: combineExposures,
  })

  /** Keyed rather than positional: `exposures` is indexed against the filtered list, not `repositories`. */
  const exposureByRepositoryId = useMemo(
    () => new Map(scoredRepositoryIds.map((repositoryId, index) => [repositoryId, exposures[index]])),
    [scoredRepositoryIds, exposures]
  )

  return useMemo(
    () =>
      new Map(
        repositories.map((repository, index) => {
          const entry = scorecards[index]
          const exposure = exposureByRepositoryId.get(repository.id)
          return [
            repository.id,
            toRiskScore({
              response: entry?.response,
              isPending: entry?.isPending ?? true,
              internetFacing: exposure?.internetFacing,
              // A repository not yet in the filtered list has no exposure query at all. Its scorecard is
              // still pending, so `isPending` above already holds the row at `loading`.
              isExposureLoading: exposure?.isLoading ?? false,
            }),
          ]
        })
      ),
    [repositories, scorecards, exposureByRepositoryId]
  )
}
